import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Form, Spinner } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import toast from "react-hot-toast";
import { useRecoveryPass, useValidarTokenRecuperacion } from "@/hooks/useAuth";
import { validateNewPassword } from "@/lib/passwordPolicy";
import { PasswordRequirements } from "@/components/PasswordRequirements";
import Logo from "@/assets/imgs/logo.png"

const ENLACE_INVALIDO_MESSAGE = "El enlace no es válido o ha caducado. Solicita uno nuevo.";

export const RecoveryPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const sendRecovery = useRecoveryPass();
  const validarToken = useValidarTokenRecuperacion();

  // "validating" | "valid" | "invalid"
  const [tokenState, setTokenState] = useState(token ? "validating" : "invalid");

  const defaultValues = {
    nueva_pass: '',
    confirmar_pass: '',
  };

  const { control, formState: { errors }, handleSubmit, watch } = useForm({ defaultValues });
  const newPasswordValue = watch("nueva_pass");

  const getFormErrorMessage = (name) =>
    errors[name] && (
      <small className="text-danger">{errors[name].message}</small>
    );

  useEffect(() => {
    if (!token) {
      setTokenState("invalid");
      return;
    }

    validarToken.mutateAsync({ token })
      .then((resp) => {
        setTokenState(resp?.valido ? "valid" : "invalid");
      })
      .catch(() => {
        setTokenState("invalid");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- se ejecuta solo una vez al montar para validar el token de la URL
  }, [token]);

  const onSubmit = (formData) => {
    sendRecovery.mutateAsync({ token, nueva_pass: formData.nueva_pass })
      .then((resp) => {
        if (resp.status === "error") {
          toast.error(resp.message);
          return;
        }
        toast.success("Contraseña restablecida con éxito");
        navigate("/login");
      })
      .catch(() => {
        toast.error("Error al restablecer contraseña");
      });
  };

  if (tokenState === "validating") {
    return (
      <div className="login-container">
        <div className="login-left-image" />
        <div className="login-form-container">
          <img src={Logo} alt="Openmatic Logo" className="login-logo" />
          <p className="login-subtitle">Validando enlace...</p>
          <div className="text-center">
            <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
          </div>
        </div>
      </div>
    );
  }

  if (tokenState === "invalid") {
    return (
      <div className="login-container">
        <div className="login-left-image" />
        <div className="login-form-container">
          <img src={Logo} alt="Openmatic Logo" className="login-logo" />
          <h2 className="login-welcome">Enlace no válido</h2>
          <p className="login-subtitle">{ENLACE_INVALIDO_MESSAGE}</p>
          <div className="mb-3 text-center">
            <Link to="/forgot-password" className="register-button-active">
              Solicitar nuevo enlace
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-left-image" />
      <div className="login-form-container">
        <img
          src={Logo}
          alt="Openmatic Logo"
          className="login-logo"
        />
        <h2 className="login-welcome">¡Te damos la bienvenida!</h2>
        <p className="login-subtitle">Restablece tu contraseña</p>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <div className="login-input-group">
            <Controller name="nueva_pass" control={control}
              rules={{
                required: 'Debe indicar la contraseña',
                validate: (value) => validateNewPassword(value) || true,
              }}
              render={({ field, fieldState }) => (
                <Form.Group className="position-relative mb-2 ">
                  <Form.Label className="mb-2" htmlFor={field.name}>Nueva contraseña <span className='text-danger'>*</span></Form.Label>
                  <Form.Control type="password" placeholder="Nueva contraseña" id={field.name} {...field} isInvalid={fieldState.invalid} />
                  {getFormErrorMessage("nueva_pass")}
                  <PasswordRequirements password={newPasswordValue} />
                </Form.Group>
              )}
            />
          </div>

          <div className="login-input-group">
            <Controller name="confirmar_pass" control={control}
              rules={{
                required: 'Debe confirmar la contraseña',
                validate: (value) => value === newPasswordValue || "Las contraseñas no coinciden",
              }}
              render={({ field, fieldState }) => (
                <Form.Group className="position-relative mb-2 ">
                  <Form.Label className="mb-2" htmlFor={field.name}>Confirmar contraseña <span className='text-danger'>*</span></Form.Label>
                  <Form.Control type="password" placeholder="Confirmar contraseña" id={field.name} {...field} isInvalid={fieldState.invalid} />
                  {getFormErrorMessage("confirmar_pass")}
                </Form.Group>
              )}
            />
          </div>

          <div className="mb-3 text-center">
            <Button type="submit" className="register-button-active" disabled={sendRecovery.isLoading}>
              {sendRecovery.isLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" role="status" aria-hidden="true"></Spinner>
                  Enviando...
                </>
              ) : (
                "Restablecer contraseña"
              )}
            </Button>
          </div>
        </Form>

      </div>
    </div>
  )
}
